<template>
    <div class="page">
        <EntityLandingHeader
            :background-image-url="backgroundImageUrl"
            :entity="performer"
        />

        <section v-if="showFilters">
            <div
                class="sports-performer-filters"
                v-if="isPerformerInLeagueSports"
            >
                <div
                    class="whereabouts-filter"
                    :class="{ 'whereabouts-filter--multiline': isMultipleLineFilters }"
                >
                    <div class="whereabouts-filter__shop-by">Shop</div>
                    <Tabs
                        :default-item="whereaboutsItems[0]"
                        :dropdown-query-param="'whereabouts'"
                        :items="whereaboutsItems"
                        @reset-tabs-dropdown="$router.push(makeWhereaboutsRoute('all'))"
                    />
                </div>
                <div class="sports-performer-filters--mobile">
                    <div class="date-filter">
                        <span class="icon-calendar-blank"></span>
                        <fx-date-range
                            align="left"
                            data-e2e="search.date-filter"
                            min-date="today"
                            v-model="dateRange"
                            :custom-option-copy="'Choose Date'"
                            :default-option-copy="'Dates'"
                            :presets="dateRangeFilterPresets"
                            @input="onDateRangeInput">
                            <span class="icon-chevron-lg-down" slot="caret"></span>
                        </fx-date-range>
                    </div>
                    <div
                        v-if="isShowOpponentsFilters"
                        class="opponent-filter">
                        <span class="opponent-filter__vs fx-hidden--small fx-hidden--medium">vs.</span>
                        <fx-select
                            v-model="opponentId"
                            :default-option="{ label: 'Opponent', value: null }"
                            :disable-native="true"
                            :options="opponents"
                            @input="onOpponentIdSelect">
                            <span class="icon-chevron-lg-down" slot="caret"></span>
                        </fx-select>
                    </div>
                </div>
            </div>

            <div
                v-else-if="!isPerformerInLeagueSports"
                class="non-league-performer-filters"
            >
                <div class="non-sports-locations-filter">
                    <span class="icon-location-marker"></span>
                    <fx-select
                        v-model="locationId"
                        :default-option="{ label: 'Any Location', value: null }"
                        :disable-native="true"
                        :options="locations"
                        @input="onLocationIdSelect">
                        <span class="icon-chevron-lg-down" slot="caret"></span>
                    </fx-select>
                </div>
                <div class="date-filter date-filter--non-sports-performer">
                    <span class="icon-calendar-blank"></span>
                    <fx-date-range
                        align="left"
                        data-e2e="search.date-filter"
                        min-date="today"
                        v-model="dateRange"
                        :custom-option-copy="'Choose Date'"
                        :default-option-copy="'Dates'"
                        :presets="dateRangeFilterPresets"
                        @input="onDateRangeInput">
                        <span class="icon-chevron-lg-down" slot="caret"></span>
                    </fx-date-range>
                </div>
            </div>
        </section>

        <section
            v-if="isPerformerInLeagueSports && whereaboutsItems[0].isActive"
            class="local-events-section"
        >
            <div class="background">

                <div class="local-performer-filter-container">
                    <h2 class="local-performer-filter-header">Near</h2>
                    <section class="local-performer-filter">
                        <LocationFilter
                            :default-location="$config.defaultLocation ? $config.defaultLocation : userLocation"
                            :location="location" :locationFilterCopy="''" @error="onLocationError"
                            @select="onLocalEventLocationSelect"
                        />
                    </section>
                </div>

                <div>
                    <EventList
                        :events="localEvents"
                        :format-points="formatPoints"
                        :is-loading-events="isLoadingLocalEvents"
                        :is-loading-more-events="isLoadingMoreLocalEvents"
                        :is-loyalty-pricing="$store.state.isRedeemingLoyaltyPoints"
                        :is-show-thumbnails="isShowThumbnails"
                        :load-more-copy="'Show More'"
                        :loading-more-copy="'Loading More Events...'"
                        :make-event-route="makeEventRoute"
                        :on-event-click="onEventClick"
                        :pagination="localEventsPagination"
                        @load-more-events="onLoadMoreLocalEvents">
                        <template slot="events-not-found">
                        <transition
                            mode="out-in"
                            name="fade">
                            <NoResults
                                :show-redirect="Boolean(!locationId && !dateRange && !opponentId && isAllEvents)"
                                :no-results-copy="'There Are No Games Nearby'"/>
                        </transition>
                        </template>
                    </EventList>
                </div>
            </div>

        </section>

        <h2
            class="scheduled-event-list-header"
            v-if="isPerformerInLeagueSports"
            :class="{ 'scheduled-event-list-header--nearby-events': isPerformerInLeagueSports && whereaboutsItems[0].isActive }"
        >
            Schedule
        </h2>
        <section>
            <EventList
                :events="events"
                :format-points="formatPoints"
                :is-loading-events="isLoadingEvents"
                :is-loading-more-events="isLoadingMoreEvents"
                :is-loyalty-pricing="$store.state.isRedeemingLoyaltyPoints"
                :is-show-thumbnails="isShowThumbnails"
                :load-more-copy="'Show More'"
                :loading-more-copy="'Loading More Events...'"
                :make-event-route="makeEventRoute"
                :on-event-click="onEventClick"
                :pagination="pagination"
                @load-more-events="onLoadMoreEvents">
                <template slot="events-not-found">
                    <transition
                        mode="out-in"
                        name="fade">
                        <NoResults
                            :show-redirect="Boolean(!locationId && !dateRange && !opponentId && isAllEvents)"
                            :no-results-copy="noEventsFoundCopy"/>
                    </transition>
                </template>
            </EventList>
        </section>
    </div>
</template>

<script>
import { EVENT_NAME, PERFORMER_LANDING_PAGE } from 'core/constants/googleAnalyticEvents'
import { getEntityLandingHeaderBackgroundUrl } from '../services/EntityService'
import { isPerformerInLeagueSports } from '../services/PerformerService'
import { mapAggregateCollectionToDropdownOptions, sortDropdownOptions } from '../services/EventAggregatesService.js'
import DateUtil from 'core/utilities/DateUtil'
import EntityLandingHeader from '../components/EntityLandingHeader.vue'
import errorHandleable from '../mixins/ErrorHandleable'
import eventFilterable from '../mixins/EventFilterable'
import eventListable from '../mixins/EventListable'
import LocationFilter from '../components/LocationFilter.vue'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import NoResults from '../components/NoResults.vue'
import quebecUserModal from '../mixins/QuebecUserModal'
import resizable from '../mixins/Resizable'
import selectComponent from 'core/components/SelectComponent.vue'
import Tabs from '../components/Tabs.vue'
import trackable from '../mixins/Trackable'

export default {
    beforeCreate () {
        this.htmlPageTitle = this.performer ? `${this.performer.name} tickets at ${this.$config.partnerName}` : `Performer tickets at ${this.$config.partnerName}`
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.prevRoute = from
        })
    },
    components: {
        'fx-select': selectComponent,
        EntityLandingHeader,
        LocationFilter,
        NoResults,
        Tabs
    },
    computed: {
        backgroundImageUrl () {
            return getEntityLandingHeaderBackgroundUrl(this.performer, this.$config.isIntegrated)
        },
        isFetchNearbyEvents () {
            return Boolean(this.isPerformerInLeagueSports && this.hasNearbyLocationChanged)
        },
        isMultipleLineFilters () {
            return (this.isShowOpponentsFilters || this.isShowLocationFilter) && this.isWindowSmall
        },
        isShowOpponentsFilters () {
            return this.opponents && this.opponents.length > 1
        },
        // There are two concepts of location filters on the performer page,
        // 1. location dropdown (non sports performer)
        // 2. autocomplete location filter (sports performer nearby events)
        isShowLocationFilter () {
            if (!this.performer) { return false }
            if (this.performer.segment_name.toLowerCase() === 'sports') { return false }
            return true
        },
        noEventsFoundCopy () {
            if (this.isPerformerInLeagueSports && this.whereabouts === this.whereaboutsTypes.HOME) { return 'No Home games available' }
            if (this.isPerformerInLeagueSports && this.whereabouts === this.whereaboutsTypes.AWAY) { return 'No Away games available' }
            return 'No Events Found'
        },
        whereaboutsItems () {
            return [
                {
                    isActive: this.isAllEvents,
                    label: 'All Games',
                    to: this.makeWhereaboutsRoute('all')
                },
                {
                    isActive: this.isHomeEvents,
                    label: this.isWindowSmall ? 'Home Games' : 'Home',
                    to: this.makeWhereaboutsRoute('home')
                },
                {
                    isActive: this.isAwayEvents,
                    label: this.isWindowSmall ? 'Away Games' : 'Away',
                    to: this.makeWhereaboutsRoute('away')
                }
            ]
        },
        initialLocationId () {
            if (this.isPerformerInLeagueSports) {
                return null
            } else {
                if (this.nonSportsFirstLoad && !this.locationId) {
                    return null
                } else {
                    return this.locationId
                }
            }
        }
    },
    created () {
        this.trackableEventCategory = 'UX - Performer Landing Page'
        this.trackPageView(this.$route.fullPath, this.htmlPageTitle)
    },
    data () {
        return {
            firstLocalEventsLoad: true,
            hasNearbyLocationChanged: false,
            hasWhereaboutsChanged: false,
            isAllEvents: true,
            isAwayEvents: false,
            isHomeEvents: false,
            isLoadingLocalEvents: true,
            isLoadingMoreLocalEvents: false,
            isPerformerInLeagueSports: false,
            isShowThumbnails: false,
            localEvents: [],
            localEventsPage: 1,
            localEventsPagination: null,
            locations: [],
            nonSportsFirstLoad: true,
            numAwayEvents: 0,
            numHomeEvents: 0,
            opponents: [],
            performer: null,
            prevRoute: null,
            showFilters: false,
            userLocation: null
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    mixins: [
        errorHandleable,
        eventFilterable,
        eventListable,
        loyaltyFormattable,
        quebecUserModal,
        resizable,
        trackable
    ],
    props: ['performerId'],
    methods: {
        onLocalEventLocationSelect (location) {
            this.hasNearbyLocationChanged = true
            this.onLocationSelected(location)
        },
        fetchLocalEvents () {
            if (!this.isPerformerInLeagueSports) { return }
            return this.$store.state.userLocationPromise
                .then(userLocation => {
                    this.userLocation = userLocation
                    return this.$marketplace.getEntityEvents('performers', this.performerId, {
                        all_in_price: this.$config.isAllInPricing ? 1 : 0,
                        date_end: this.serializedEndDate,
                        date_start: this.serializedStartDate,
                        latitude: this.$route.query.lat,
                        location_id: this.location ? this.location.id : null,
                        longitude: this.$route.query.lon,
                        opponent_id: this.opponentId,
                        page: this.localEventsPage,
                        per_page: 5,
                        utc_offset: DateUtil.format(new Date(), 'Z')
                    })
                        .then(response => {
                            return {
                                events: response.chronological.results,
                                pagination: response.chronological.meta
                            }
                        })
                })
        },
        fetchEvents () {
            return this.$store.state.userLocationPromise
                .then(userLocation => {
                    return this.$marketplace.getEntityEvents('performers', this.performerId, {
                        all_in_price: this.$config.isAllInPricing ? 1 : 0,
                        date_end: this.serializedEndDate,
                        date_start: this.serializedStartDate,
                        location_id: this.initialLocationId,
                        opponent_id: this.opponentId,
                        page: this.page,
                        per_page: 5,
                        utc_offset: DateUtil.format(new Date(), 'Z'),
                        whereabouts: this.whereabouts
                    })
                })
                .then(response => {
                    this.numAwayEvents = (response.chronological.aggs.performers || {}).away_game_count || 0
                    this.numHomeEvents = (response.chronological.aggs.performers || {}).home_game_count || 0
                    this.locations = sortDropdownOptions(mapAggregateCollectionToDropdownOptions(response.chronological.aggs.location || []))
                    this.opponents = sortDropdownOptions(mapAggregateCollectionToDropdownOptions((response.chronological.aggs.performers || {}).opponents || []))

                    if (this.firstLocalEventsLoad && this.isPerformerInLeagueSports) {
                        this.firstLocalEventsLoad = false
                        this.loadLocalEvents()
                    }

                    if (this.numHomeEvents < 1 && this.numAwayEvents > 0 && !this.hasWhereaboutsChanged) {
                        this.setWhereabouts(this.whereaboutsTypes.AWAY)
                        this.hasWhereaboutsChanged = true
                    }

                    return {
                        events: response.chronological.results,
                        pagination: response.chronological.meta
                    }
                })
        },
        makeWhereaboutsRoute (key) {
            const query = { ...this.$route.query }

            if (key) {
                query.whereabouts = key
            }
            return { query }
        },
        loadLocalEvents () {
            this.isLoadingLocalEvents = this.page === 1
            this.isLoadingMoreLocalEvents = !this.isLoadingLocalEvents

            this.fetchLocalEvents()
                .then(({ events, pagination }) => {
                    const nextEvents = events.map(rawEvent => this.parseEvent(rawEvent))
                    this.localEvents = this.localEventsPage === 1 ? nextEvents : this.localEvents.concat(nextEvents)
                    this.localEventsPagination = pagination
                })
                .catch(err => this.catchAndNotify(err))
                .finally(() => {
                    this.isLoadingLocalEvents = false
                    this.isLoadingMoreLocalEvents = false
                })
        },
        onLoadMoreLocalEvents () {
            if (this.localEventsPagination && this.localEventsPage >= this.localEventsPagination.last_page) { return }
            this.localEventsPage += 1
            this.loadLocalEvents()
        },
        onLoadMoreEvents () {
            this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.LOAD_MORE_EVENTS_SELECTED)
            this.loadMoreEvents()
        },
        onDateRangeInput (dateRange) {
            this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.DATE_RANGE_FILTER_APPLIED)
            // TODO: Delete this event from GA
            this.trackClickEvent('Performer Date Filter - Selected')
            this.onDateRangeSelected(dateRange)

            if (this.isPerformerInLeagueSports) {
                this.loadLocalEvents()
            }
        },
        onEventClick () {
            this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.EVENT_SELECTED)
            // TODO: Delete this event from GA
            this.trackClickEvent('Select Event on Performer Page')
        },
        onLocationIdSelect (locationId) {
            this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.LOCATION_FILTER_CHANGED)
            this.onLocationIdSelected(locationId)
        },
        onOpponentIdSelect (opponentId) {
            this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.OPPONENT_SELECTED_APPLIED)
            this.onOpponentIdSelected(opponentId)
            this.loadLocalEvents()
        },
        onWhereaboutsClick (whereaboutsType) {
            // TODO: Delete this event from GA
            this.trackClickEvent('Sports Performer Filter Clicked')
            this.setWhereabouts(whereaboutsType)

            if (whereaboutsType === 'all') {
                this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.LOCATION_FILTER_ALL_APPLIED)
            } else if (whereaboutsType === 'away') {
                this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.LOCATION_FILTER_AWAY_APPLIED)
            } else {
                this.$googleTagManager.pushEvent(EVENT_NAME, PERFORMER_LANDING_PAGE.LOCATION_FILTER_HOME_APPLIED)
            }
        }
    },
    watch: {
        performerId: {
            immediate: true,
            handler () {
                this.performer = null

                this.$marketplace
                    .getPerformer(this.performerId)
                    .then(performer => {
                        this.performer = performer
                        this.showFilters = true
                        this.isPerformerInLeagueSports = isPerformerInLeagueSports(this.performer, this.$config.isIntegrated)
                        if (!this.isPerformerInLeagueSports && this.nonSportsFirstLoad) {
                            if (this.prevRoute && this.prevRoute.name === 'event') {
                                this.nonSportsFirstLoad = !this.nonSportsFirstLoad
                            } else {
                                this.nonSportsFirstLoad = !this.nonSportsFirstLoad
                            }
                        }
                        this.$emit('updateHead')
                    })
                    .catch(err => this.catchAndNotify(err))
            }
        },
        $route: {
            immediate: true,
            handler () {
                this.isAllEvents = Boolean(this.$route.query.whereabouts === 'all' || !this.$route.query.whereabouts)
                this.isAwayEvents = Boolean(this.$route.query.whereabouts === 'away')
                this.isHomeEvents = Boolean(this.$route.query.whereabouts === 'home')
            }
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.page {
    margin-top: -65px;
    padding: 0 var(--site-padding) 48px;
    width: 100%;

    @media (--mq-full-min) {
        margin: -100px auto 0;
        width: var(--site-container-width);
    }
}

.whereabouts {
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, .1);
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    justify-content: space-between;
    list-style: none;
    margin-bottom: 10px;
    padding: 0;

    @media (--mq-large-min) {
        margin-bottom: 0px;
    }

    & .whereabouts__item {
        & .whereabouts__button {
            background-color: #fff;
            border-bottom: 1px solid var(--c-primary);
            border-left: 1px solid var(--c-primary);
            border-right: none;
            border-top: 1px solid var(--c-primary);
            color: var(--c-primary);
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
            min-width: 100px;
            padding: 5px 16px;
            white-space: nowrap;

            &:focus,
            &:hover {
                background-color: #F0F0F0;
                color: var(--c-primary);
                outline: none;
            }
            &:disabled {
                background-color: color(var(--c-primary) shade(20%));
                border-color: color(var(--c-primary) shade(20%));
                color: white;
            }

            &:disabled {
                cursor: default;
                opacity: 1;
            }
        }
        &:first-child {
            & .whereabouts__button {
                border-bottom-left-radius: 2px;
                border-top-left-radius: 2px;
            }
        }
        &:last-child {
            & .whereabouts__button {
                border-bottom-right-radius: 2px;
                border-right: 1px solid var(--c-primary);
                border-top-right-radius: 2px;
                &:disabled {
                    border-color: color(var(--c-primary) shade(20%));
                }
            }
        }
    }

    @media (--mq-large-min) {
        flex-grow: 0;
        justify-content: flex-start;
    }
}

.local-performer-filter-container {
    align-items: center;
    display: flex;
}

.local-performer-filter-header {
    color: #444649;
    font-size: 20px;
    font-weight: 400;
    margin: 14px 0 14px 0;
    padding-top: 0px;

    @media (--mq-large-min) {
        font-size: 24px;
        font-weight: 400;
    }
}

.local-performer-filter {
    margin-left: 11px;
    & >>> .location-filter .selection {
        & span {
            color: #444649;
            font-size: 20px;
            font-weight: 400;
            letter-spacing: 0;

            @media (--mq-large-min) {
                font-size: 24px;
            }
        }
        & .open-menu-icon {
            & .icon-chevron-lg-down {
                font-size: 15px;
                top: 2px;
            }
        }
    }
    & >>> .icon-location-marker {
        font-size: 18px;
        top: 2px;

        @media (--mq-large-min) {
            top: 6px;
        }
    }
}

.scheduled-event-list-header {
    color: #444649;
    font-size: 20px;
    font-weight: 400;
    margin: 23px 0 16px 0;
    padding-top: 0px;

    &.scheduled-event-list-header--nearby-events {
        margin: 20px 0 16px 0;

        @media (--mq-large-min) {
            margin: 40px 0 16px 0;

        }
    }

    @media (--mq-large-min) {
        font-size: 24px;
        font-weight: 400;
    }
}

.local-events-section {
    @media (--mq-large-min) {
        border-bottom: 1px solid #5D5F63;
        padding-bottom: 32px;
    }
}

.local-events-horizontal-rule {
    display: none;
    margin-bottom: 31px;
    margin-top: 24px;

    @media (--mq-large-min) {
        display: block;
    }
}

>>> .fx-select-dropdown {
    z-index: 40 !important;
}

>>> .fx-date-picker--desktop {
    z-index: 40 !important;
    -webkit-transform: translate3d(0,0,0);
    /* work around for safari issues with z-index */
}

.background {
    background-color: #E7E9E9;
    margin: 0 -16px 0px -16px;
    padding: 0 16px 16px 16px;

    @media (--mq-large-min) {
        margin: initial;
        padding: initial;
        background-color: #F4F4F4;
    }
}

.sports-performer-filters {
    margin-bottom: 8px;
    -webkit-font-smoothing: antialiased;
    color: #808689;
    display: flex;
    flex-direction: column;
    @media (--mq-large-min) {
        flex-direction: row;
    }
}

.sports-performer-filters--mobile {
    display: flex;
    flex-flow: wrap;
}

.non-league-performer-filters {
    -webkit-font-smoothing: antialiased;
    color: #808689;
    display: flex;
    flex-flow: wrap;
    margin-bottom: 8px;
}

.filters__shop-by {
    color: #444649;
    font-size: 20px;
    font-weight: 400;
    letter-spacing: 0;
    margin-right: 7px;

    @media (--mq-large-min) {
        display: none;
    }
}

.whereabouts-filter {
    display: flex;
    flex-direction: row;
    padding-right: 12px;

    @media (--mq-large-min) {
        flex-direction: column;
        padding-right: 0px;

    }

}

.whereabouts-filter--multiline {
    margin-bottom: 8px;
}

.whereabouts-filter__shop-by {
    color: #444649;
    font-size: 20px;
    font-weight: 400;
    letter-spacing: 0;
    margin-right: 7px;

    @media (--mq-large-min) {
        display: none;
    }
}

.opponent-filter {
    padding: 0;
    padding-right: 12px;
    display: flex;
    align-items: center;
    margin-left: 0;

    & >>> li {
        font-size: 16px;
    }
    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .fx-selection {
        color: #808689;
        font-size: var(--fs-large);
        text-decoration: underline;
    }

    & >>> .icon-cancel {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }
    & >>> .icon-chevron-lg-down {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }
}

.opponent-filter__vs {
    font-size: 16px;
    margin-right: 4px;
}

.date-filter {
    padding: 0;
    padding-right: 12px;
    display: flex;
    align-items: center;
    margin-left: 0;

    @media (--mq-large-min) {
        margin-left: 12px;
        &.date-filter--non-sports-performer {
            margin-left: 0;
        }
    }

    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .fx-selection {
        color: #808689;
        font-size: var(--fs-large);
        text-decoration: underline;
    }

    & >>> .icon-cancel {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }
    & >>> .icon-chevron-lg-down {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }

    & >>> .icon-calendar-blank {
        color: #808689;
        font-size: 16px;
        margin-right: 4px;
        position: relative;
        top: 2px;
    }
}

.non-sports-locations-filter {
    align-items: center;
    display: flex;
    margin-left: 0;
    margin-right: 12px;
    padding: 0;

    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .fx-selection {
        color: #808689;
        font-size: var(--fs-large);
        text-decoration: underline;
    }

    & >>> .icon-cancel {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }
    & >>> .icon-chevron-lg-down {
        color: #808689;
        font-size: 16px;
        position: relative;
        right: 3px;
        top: 3px;
    }

    & >>> .icon-location-marker {
        color: #808689;
        font-size: 16px;
        position: relative;
        top: 2px;
    }
}
</style>
